import { useState, useEffect } from "react";
import { api } from "../api";
import FixedCard from "../components/FixedCard";
import SEO from "../components/SEO";

const catColors = {
  "Sexual Health":"#8A1613","Mental Health":"#534AB7","Tuberculosis":"#007965",
  "HIV/AIDS":"#8A1613","Diabetes":"#B07D1A","Maternal Health":"#007965",
  "Environmental Health":"#3B6D11","Health Rights":"#8A1613",
  "Community Care":"#007965","Podcast Notes":"#534AB7",
};

export default function HealthArchive() {
  const [articles, setArticles] = useState([]);
  const [cats,     setCats]     = useState([]);
  const [filter,   setFilter]   = useState("All");
  const [fullArticle, setFullArticle] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([api.getArticles(), api.getArticleCats()])
      .then(([a, c]) => { setArticles(a); setCats(["All", ...c]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? articles : articles.filter(a => a.category === filter);

  // Fetch full article (with body) on card click
  const openArticle = async (article) => {
    if (article.body) { setFullArticle(article); return; }
    try { const full = await api.getArticle(article.slug); setFullArticle(full); }
    catch { setFullArticle(article); }
  };

  return (
    <>
    <SEO
      title="Health Archive"
      path="/health-archive"
      description="Youth health stories, podcast episodes, campaign updates and practical health information in simple, accessible language. Covering HIV, TB, mental health, sexual health and more across South Africa."
    />
    <div style={{ paddingTop:"5rem" }}>

      {/* Hero */}
      <section style={{ background:"#0B0B0B", padding:"6rem 2rem 5rem", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
          {["HEALTH","ARCHIVE","STORIES","INFO"].map((w,i)=>(
            <span key={i} style={{ position:"absolute", fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(6rem,14vw,16rem)", color:"transparent", WebkitTextStroke:"1px rgba(149,187,131,0.06)", top:`${i*25}%`, right:`${(i%2)*30}%`, whiteSpace:"nowrap", userSelect:"none" }}>{w}</span>
          ))}
        </div>
        <div className="container" style={{ position:"relative", zIndex:1 }}>
          <p className="section-label" style={{ color:"#95BB83" }}>Health Archive</p>
          <h1 style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(2.8rem,7vw,6rem)", color:"#fff", lineHeight:0.95, letterSpacing:"-0.03em", marginBottom:"1.25rem" }}>
            Health<br /><span style={{ color:"#8A1613" }}>Archive.</span>
          </h1>
          <p style={{ color:"rgba(255,255,255,0.5)", maxWidth:520, fontFamily:"var(--font-body)", fontSize:"1.05rem", lineHeight:1.75 }}>
            Building a digital storage space for youth health stories, podcast episodes, campaign updates, practical health information and news that answers your questions in simple, accessible language.
          </p>
        </div>
      </section>

      {/* Articles */}
      <section style={{ background:"#F7F1DF", padding:"5rem 2rem 6rem" }}>
        <div className="container">

          {/* Filter pills */}
          <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap", marginBottom:"2.5rem" }}>
            {cats.map(c => (
              <button key={c} onClick={() => setFilter(c)} style={{
                background: filter===c ? "#8A1613" : "#fff",
                color: filter===c ? "#fff" : "rgba(11,11,11,0.55)",
                border: `1px solid ${filter===c ? "#8A1613" : "rgba(11,11,11,0.12)"}`,
                borderRadius:100, padding:"0.4rem 1.1rem", fontSize:"0.8rem",
                fontFamily:"var(--font)", fontWeight:600, cursor:"pointer", transition:"all 0.2s",
              }}>{c}</button>
            ))}
          </div>

          {loading && <p style={{ color:"rgba(11,11,11,0.4)", fontFamily:"var(--font-body)" }}>Loading articles…</p>}
          {!loading && filtered.length === 0 && (
            <div style={{ textAlign:"center", padding:"4rem 0" }}>
              <p style={{ fontSize:"3rem", marginBottom:"1rem" }}>📝</p>
              <p style={{ color:"rgba(11,11,11,0.4)", fontFamily:"var(--font-body)" }}>No articles in this category yet.</p>
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"1.25rem" }}>
            {filtered.map(article => {
              const color = catColors[article.category] || "#8A1613";
              return (
                <FixedCard
                  key={article.id}
                  height={320}
                  headerHeight={150}
                  accentColor={color}
                  headerContent={
                    article.cover_url
                      ? <img src={article.cover_url} alt={article.title}
                          style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
                      : <span style={{ fontFamily:"var(--font)", fontSize:"3.5rem", fontWeight:900, color:"rgba(255,255,255,0.15)", zIndex:1, position:"relative" }}>📝</span>
                  }
                  eyebrow={article.category}
                  title={article.title}
                  meta={`${article.author} · ${new Date(article.publish_date).toLocaleDateString("en-ZA",{year:"numeric",month:"short",day:"numeric"})}`}
                  body={article.excerpt}
                  previewLines={2}
                  modalTitle={article.title}
                  modalSubtitle={article.category}
                  modalIcon={article.cover_url}
                  modalIsPhoto={!!article.cover_url}
                  onClick={() => openArticle(article)}
                  modalExtra={
                    <p style={{ fontFamily:"var(--font-body)", fontSize:"0.75rem", color:"rgba(11,11,11,0.35)", fontStyle:"italic" }}>
                      ⚕ For educational purposes only — does not replace professional medical advice.
                    </p>
                  }
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Full article modal */}
      {fullArticle && (
        <div onClick={() => setFullArticle(null)} style={{
          position:"fixed", inset:0, zIndex:300,
          background:"rgba(0,0,0,0.75)", backdropFilter:"blur(8px)",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"2rem 1rem", overflowY:"auto",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:"#fff", borderRadius:24, width:"100%", maxWidth:720,
            overflow:"hidden", marginTop:"2rem", marginBottom:"2rem",
            boxShadow:"0 32px 80px rgba(0,0,0,0.3)",
          }}>
            {/* Cover */}
            <div style={{
              height:260, position:"relative", overflow:"hidden",
              background:`linear-gradient(135deg,${catColors[fullArticle.category]||"#8A1613"},rgba(149,187,131,0.7))`,
              display:"flex", alignItems:"flex-end",
            }}>
              {fullArticle.cover_url && (
                <img src={fullArticle.cover_url} alt={fullArticle.title}
                  style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
              )}
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,0.7) 0%,transparent 60%)" }} />
              <div style={{ position:"relative", zIndex:1, padding:"1.75rem 2rem" }}>
                <span style={{ display:"inline-block", background:catColors[fullArticle.category]||"#8A1613", color:"#fff", borderRadius:100, padding:"0.25rem 0.85rem", fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"var(--font)", marginBottom:"0.6rem" }}>{fullArticle.category}</span>
                <h2 style={{ fontFamily:"var(--font)", fontWeight:900, fontSize:"clamp(1.3rem,3vw,1.9rem)", color:"#fff", lineHeight:1.2, letterSpacing:"-0.02em", margin:0 }}>{fullArticle.title}</h2>
              </div>
              <button onClick={() => setFullArticle(null)} style={{ position:"absolute", top:16, right:16, zIndex:2, background:"rgba(0,0,0,0.5)", border:"none", borderRadius:"50%", width:40, height:40, color:"#fff", cursor:"pointer", fontSize:"1.1rem", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            </div>

            <div style={{ padding:"2rem 2.5rem 2.5rem" }}>
              <div style={{ display:"flex", gap:"1rem", marginBottom:"1.5rem", paddingBottom:"1.25rem", borderBottom:"1px solid rgba(11,11,11,0.08)", flexWrap:"wrap" }}>
                <span style={{ fontSize:"0.8rem", color:"rgba(11,11,11,0.45)", fontFamily:"var(--font-body)" }}>✍️ {fullArticle.author}</span>
                <span style={{ color:"rgba(11,11,11,0.2)" }}>·</span>
                <span style={{ fontSize:"0.8rem", color:"rgba(11,11,11,0.45)", fontFamily:"var(--font-body)" }}>📅 {new Date(fullArticle.publish_date).toLocaleDateString("en-ZA",{year:"numeric",month:"long",day:"numeric"})}</span>
              </div>
              <p style={{ fontFamily:"var(--font-body)", fontSize:"1.05rem", color:"rgba(11,11,11,0.65)", lineHeight:1.75, marginBottom:"1.5rem", fontStyle:"italic", borderLeft:`3px solid ${catColors[fullArticle.category]||"#8A1613"}`, paddingLeft:"1rem" }}>{fullArticle.excerpt}</p>
              <div style={{ fontFamily:"var(--font-body)", fontSize:"0.97rem", color:"#1a1a1a", lineHeight:1.85 }} dangerouslySetInnerHTML={{ __html: fullArticle.body || "<p>Full content coming soon.</p>" }} />
              <div style={{ marginTop:"2rem", paddingTop:"1.25rem", borderTop:"1px solid rgba(11,11,11,0.08)" }}>
                <p style={{ fontFamily:"var(--font-body)", fontSize:"0.75rem", color:"rgba(11,11,11,0.35)", fontStyle:"italic" }}>⚕ For educational purposes only — does not replace professional medical advice.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .article-body h2,.article-body h3{font-family:var(--font);font-weight:800;color:#0B0B0B;margin:1.5rem 0 0.6rem}
        .article-body h3{font-size:1.1rem;color:#8A1613}
        .article-body p{margin:0 0 1rem}
        .article-body ul,.article-body ol{padding-left:1.5rem;margin:0.75rem 0 1rem}
        .article-body li{margin-bottom:0.4rem;line-height:1.7}
        .article-body strong{color:#0B0B0B;font-weight:700}
        .article-body a{color:#8A1613;text-decoration:underline}
        .article-body blockquote{border-left:3px solid #8A1613;padding:0.5rem 0 0.5rem 1rem;margin:1rem 0;font-style:italic;color:rgba(11,11,11,0.6)}
        .article-body img{max-width:100%;border-radius:10px;margin:1rem 0}
      `}</style>
    </div>
    </>
  );
}
